import { FC, useMemo, useState } from "react"
import { Button, MenuItem, Stack, TextField, Tooltip } from "@mui/material"
import { SnackbarAlert } from "@/core/components"
import {
  useBotsListQuery,
  useTemplatesListQuery,
  useCreateBindingMutation,
} from "../../store"

interface IProps {
  onDone?: () => void
}

export const BindingForm: FC<IProps> = ({ onDone }) => {
  const { data: botsRes } = useBotsListQuery({
    queryParams: { limit: 200, offset: 0 },
  })
  const { data: templatesRes } = useTemplatesListQuery({
    queryParams: { limit: 200, offset: 0 },
  })
  const bots = botsRes?.items ?? []
  const templates = templatesRes?.items ?? []

  const [createBinding, { isLoading }] = useCreateBindingMutation()

  const [botId, setBotId] = useState("")
  const [templateId, setTemplateId] = useState("")
  const [triggerText, setTriggerText] = useState("")
  const [snackbar, setSnackbar] = useState<{
    variant: "success" | "error"
    message: string
  } | null>(null)

  const canSave = useMemo(
    () => !!botId && !!templateId && !isLoading,
    [botId, templateId, isLoading],
  )

  const save = async () => {
    try {
      await createBinding({
        body: {
          botId,
          templateId,
          // Пустой триггер => fallback (null); нормализацию делает бэкенд.
          triggerText: triggerText.trim() ? triggerText : null,
        },
      }).unwrap()
      setSnackbar({ variant: "success", message: "Привязка добавлена" })
      onDone?.()
    } catch (e) {
      const status = (e as { status?: number })?.status
      setSnackbar({
        variant: "error",
        message:
          status === 409
            ? "Привязка для этой пары «бот + триггер» уже существует"
            : status === 404
              ? "Бот или шаблон не найдены"
              : "Не удалось добавить привязку",
      })
    }
  }

  return (
    <Stack spacing={2} mt={1}>
      <TextField
        select
        label="Бот"
        value={botId}
        onChange={(e) => setBotId(e.target.value)}
        required
        fullWidth
      >
        {bots.map((b) => (
          <MenuItem key={b.id} value={b.id}>
            {b.username ?? b.botId}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Шаблон"
        value={templateId}
        onChange={(e) => setTemplateId(e.target.value)}
        required
        fullWidth
      >
        {templates.map((t) => (
          <MenuItem key={t.id} value={t.id}>
            {t.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Триггер (текст)"
        helperText="Пусто — ответ по умолчанию (fallback)"
        value={triggerText}
        onChange={(e) => setTriggerText(e.target.value)}
        fullWidth
      />
      <Tooltip title="Добавить привязку бота к шаблону" arrow>
        <span>
          <Button
            variant="contained"
            fullWidth
            disabled={!canSave}
            onClick={save}
          >
            Добавить
          </Button>
        </span>
      </Tooltip>
      <SnackbarAlert
        isOpen={!!snackbar}
        onClose={() => setSnackbar(null)}
        variant={snackbar?.variant}
        message={snackbar?.message ?? ""}
      />
    </Stack>
  )
}
